import { useSyncExternalStore } from "react";
import { authClient } from "@/lib/auth";

export interface SessionUser {
  id: string;
  email: string | null;
  name: string | null;
  image: string | null;
}

export interface SessionInfo {
  id: string;
  userId: string;
  expiresAt: Date | string;
  token?: string;
}

export type OAuthProvider = "google" | "github";

export interface SessionState {
  user: SessionUser | null;
  session: SessionInfo | null;
  isLoading: boolean;
  isSignedIn: boolean;
  signIn: (provider: OAuthProvider, callbackURL?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const sessionAtom = authClient.useSession;

const subscribe = (notify: () => void) => sessionAtom.listen(() => notify());

const getSnapshot = () => sessionAtom.get();

function toSessionUser(raw: unknown): SessionUser | null {
  if (!raw || typeof raw !== "object") return null;
  const u = raw as Record<string, unknown>;
  if (typeof u.id !== "string") return null;
  return {
    id: u.id,
    email: typeof u.email === "string" ? u.email : null,
    name: typeof u.name === "string" ? u.name : null,
    image: typeof u.image === "string" ? u.image : null,
  };
}

function toSessionInfo(raw: unknown): SessionInfo | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Record<string, unknown>;
  if (typeof s.id !== "string" || typeof s.userId !== "string") return null;
  const expiresAt = s.expiresAt;
  if (!(expiresAt instanceof Date) && typeof expiresAt !== "string") return null;
  return {
    id: s.id,
    userId: s.userId,
    expiresAt,
    token: typeof s.token === "string" ? s.token : undefined,
  };
}

export async function signInWithProvider(
  provider: OAuthProvider,
  callbackURL = "/auth/callback",
): Promise<void> {
  await authClient.signIn.social({ provider, callbackURL });
}

export async function signOut(): Promise<void> {
  await authClient.signOut();
}

export function useSession(): SessionState {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const data = state.data as { user?: unknown; session?: unknown } | null;
  const user = toSessionUser(data?.user);
  const session = toSessionInfo(data?.session);
  return {
    user,
    session,
    isLoading: state.isPending,
    isSignedIn: !!user,
    signIn: signInWithProvider,
    signOut,
  };
}
